import { useEffect, useState } from 'react';
import clsx from 'clsx';
import normalSrc from '../../../assets/trucky/normal.png';
import waveSrc from '../../../assets/trucky/wave.png';
import thinkingSrc from '../../../assets/trucky/thinking.png';
import workingSrc from '../../../assets/trucky/working.png';
import successSrc from '../../../assets/trucky/success.png';

export type TruckyState = 'normal' | 'wave' | 'thinking' | 'working' | 'success';

const FRAMES: Record<TruckyState, string> = {
  normal: normalSrc,
  wave: waveSrc,
  thinking: thinkingSrc,
  working: workingSrc,
  success: successSrc,
};

interface Props {
  state: TruckyState;
  className?: string;
  alt?: string;
  /** When true and state is `normal`, Trucky briefly waves every 6–9s.
   *  Used in the chat header so he feels alive while idle. */
  idleWave?: boolean;
}

/**
 * Trucky — animated chatbot avatar.
 *
 * `state` selects the displayed pose. Two states have built-in motion:
 *  - `working`: alternates working ↔ thinking every 650ms so he visibly
 *    "crunches" while the AI is streaming a response.
 *  - `normal` with `idleWave`: every 6–9s, ducks into `wave` for 1.4s
 *    so the header avatar doesn't feel static during a conversation.
 */
export function TruckyAvatar({ state, className, alt = 'Trucky', idleWave = false }: Props) {
  // Internal frame for the working ↔ thinking shuffle.
  const [workingTick, setWorkingTick] = useState(false);
  useEffect(() => {
    if (state !== 'working') {
      setWorkingTick(false);
      return;
    }
    const id = window.setInterval(() => setWorkingTick((v) => !v), 650);
    return () => window.clearInterval(id);
  }, [state]);

  // Idle-wave: random 3–5s between waves, each wave lasts ~1.4s. The
  // shorter cadence keeps the floating button and empty-state hero
  // visibly alive without crossing into "annoyingly twitchy".
  const [idleWaving, setIdleWaving] = useState(false);
  useEffect(() => {
    if (!idleWave || state !== 'normal') {
      setIdleWaving(false);
      return;
    }
    let waveTimer: number | undefined;
    const schedule = () => {
      const next = 3000 + Math.random() * 2000;
      window.setTimeout(() => {
        setIdleWaving(true);
        waveTimer = window.setTimeout(() => {
          setIdleWaving(false);
          schedule();
        }, 1400);
      }, next);
    };
    schedule();
    return () => {
      if (waveTimer) window.clearTimeout(waveTimer);
    };
  }, [idleWave, state]);

  let src: string;
  if (state === 'working') {
    src = workingTick ? FRAMES.thinking : FRAMES.working;
  } else if (state === 'normal' && idleWaving) {
    src = FRAMES.wave;
  } else {
    src = FRAMES[state];
  }

  return (
    <img
      src={src}
      alt={alt}
      draggable={false}
      className={clsx('select-none object-contain', className)}
    />
  );
}
